import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'src/shared/components/ui/accordion';
import { Badge } from 'src/shared/components/ui/badge';
import Autocomplete from 'src/shared/components/autocomplete/Autocomplete';
import type { SiteFilterIsicCode } from 'src/app/portfolio/state/api/siteFilterOptionsQuery';

type Props = {
    isicCodes: SiteFilterIsicCode[];
    isLoading: boolean;
    selectedValues: string[];
    onChange: (values: string[]) => void;
};

const MAX_BADGE_LABEL_LENGTH = 25;

const formatIsicLabel = (code: string, description: string) => `[${code}] ${description}`;

const truncateLabel = (value: string, maxLength = MAX_BADGE_LABEL_LENGTH) => {
    if (value.length <= maxLength) return value;
    return `${value.slice(0, maxLength - 1)}…`;
};

const ActivitiesFilterAccordion = ({ isicCodes, isLoading, selectedValues, onChange }: Props) => {
    const [inputValue, setInputValue] = useState('');

    const options = isicCodes.map(c => ({ value: c.code, label: formatIsicLabel(c.code, c.description) }));

    const handleSelect = (code: string | null) => {
        if (code && !selectedValues.includes(code)) {
            onChange([...selectedValues, code]);
        }
        setInputValue('');
    };

    const handleRemove = (code: string) => {
        onChange(selectedValues.filter(v => v !== code));
    };

    const labelFor = (code: string) => {
        const match = isicCodes.find(c => c.code === code);
        return match ? formatIsicLabel(match.code, match.description) : code;
    };

    return (
        <Accordion type='single' collapsible>
            <AccordionItem value='activities'>
                <AccordionTrigger data-testid={selectedValues.length > 0 ? 'activities-filter-count' : undefined}>
                    Activities{selectedValues.length > 0 && ` (${selectedValues.length})`}
                </AccordionTrigger>
                <AccordionContent>
                    <div className='flex flex-col gap-2'>
                        {isLoading ? (
                            <span className='text-sm text-muted-foreground'>Loading activities…</span>
                        ) : (
                            <Autocomplete
                                options={options}
                                value=''
                                inputValue={inputValue}
                                onInputChange={setInputValue}
                                onChange={handleSelect}
                                placeholder='Search activities…'
                                showAllOnOpen
                                updateInputOnSelect={false}
                            />
                        )}
                        {selectedValues.length > 0 && (
                            <div className='flex flex-wrap gap-2' aria-label='Selected activities'>
                                {selectedValues.map(code => (
                                    <Badge key={code} variant='outline' className='flex items-center gap-1'>
                                        <span title={labelFor(code)}>{truncateLabel(labelFor(code))}</span>
                                        <button
                                            type='button'
                                            aria-label={`Remove ${labelFor(code)}`}
                                            onClick={() => handleRemove(code)}
                                            className='inline-flex items-center justify-center text-muted-foreground hover:text-black'
                                        >
                                            <X className='h-3.5 w-3.5' />
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        )}
                    </div>
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
};

export default ActivitiesFilterAccordion;
